'use client';

import { useState, useEffect, useRef, useCallback, useId } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getDownloadResource } from '@/lib/downloadResources';
import { validateLead, normaliseEmail, isEmailSyntaxValid } from '@/lib/leadValidation';

/**
 * Download-lead notifications go through the site's own
 * `/api/submit-lead` route, which forwards them to FormSubmit SERVER-side
 * (see src/app/api/submit-lead/route.ts). The browser only ever makes
 * same-origin requests, so visitors' ad blockers, privacy extensions and
 * antivirus web filters cannot break the form — and the FormSubmit endpoint
 * never appears in client JavaScript at all.
 */
const SUBMIT_LEAD_URL = '/api/submit-lead';

/** Abort the notification request if it hangs longer than this. */
const SUBMIT_TIMEOUT_MS = 20_000;

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Stable resource ID from `src/lib/downloadResources.ts`. Never a file path. */
  resourceId: string;
  buttonText?: string;
  modalTitle?: string;
  modalDescription?: string;
  successTitle?: string;
  successMessage?: string;
}

type Phase = 'idle' | 'validating-email' | 'submitting' | 'preparing-download' | 'done';

interface EmailCheck {
  status: 'idle' | 'checking' | 'valid' | 'invalid';
  /** True only when an external provider actually verified the address. */
  externallyVerified?: boolean;
  message?: string;
}

const isDev = process.env.NODE_ENV !== 'production';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export default function LeadCaptureModal({
  isOpen,
  onClose,
  resourceId,
  buttonText = 'Download',
  modalTitle = 'Download',
  modalDescription = 'Enter your details to download',
  successTitle = 'Thank You!',
  successMessage = 'Your download has started.',
}: LeadCaptureModalProps) {
  const [fullName, setFullName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ fullName?: string; workEmail?: string }>({});
  const [emailCheck, setEmailCheck] = useState<EmailCheck>({ status: 'idle' });
  const [phase, setPhase] = useState<Phase>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Set once the lead is delivered but the scripted blob download failed —
      offers the native browser download, which security software does not
      intercept the way it can gut a fetch() response body. */
  const [directUrl, setDirectUrl] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);
  /** Hard guard against double submission, immune to React state batching. */
  const inFlight = useRef(false);
  /** True once FormSubmit accepted this visit's details — a retry after a
      failed file fetch must not send a second notification email. */
  const notified = useRef(false);

  const titleId = useId();
  const descriptionId = useId();

  /** The download target always comes from the predefined registry — a
      visitor-controlled URL can never become the download URL. */
  const resource = getDownloadResource(resourceId);

  const busy = phase === 'submitting' || phase === 'preparing-download';

  const resetAll = useCallback(() => {
    setFullName('');
    setWorkEmail('');
    setFieldErrors({});
    setEmailCheck({ status: 'idle' });
    setPhase('idle');
    setSubmitError(null);
    setDirectUrl(null);
    inFlight.current = false;
    notified.current = false;
  }, []);

  const requestClose = useCallback(() => {
    if (busy) return; // never close mid-request
    onClose();
  }, [busy, onClose]);

  /* ---------------- focus management + scroll lock ---------------- */

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Lock background scroll without causing a layout jump.
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    const focusTimer = setTimeout(() => nameRef.current?.focus(), 0);

    return () => {
      clearTimeout(focusTimer);
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  // Reset form state whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) resetAll();
  }, [isOpen, resetAll]);

  // Escape to close + focus trap.
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const list = Array.from(nodes).filter((n) => n.offsetParent !== null);
      if (list.length === 0) return;

      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, requestClose]);

  /* ---------------- debounced advisory email check ---------------- */

  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const candidate = normaliseEmail(workEmail);

    if (!candidate) {
      setEmailCheck({ status: 'idle' });
      return;
    }

    if (!isEmailSyntaxValid(candidate)) {
      setEmailCheck({ status: 'idle' }); // don't nag mid-typing; validated on submit
      return;
    }

    const seq = ++requestSeq.current;
    setEmailCheck({ status: 'checking' });

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/validate-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: candidate }),
        });
        const data = await res.json();
        if (seq !== requestSeq.current) return; // a newer keystroke won

        setEmailCheck(
          data?.isValid
            ? { status: 'valid', externallyVerified: data.externallyVerified === true }
            : { status: 'invalid', message: data?.error || 'Please check this address.' }
        );
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (isDev) console.error('[LeadCaptureModal] email check failed', err);
        // Advisory check only — do not block the user on a network blip.
        setEmailCheck({ status: 'idle' });
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [workEmail, isOpen]);

  /* ---------------- submit ---------------- */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (inFlight.current) return; // duplicate click guard
    setSubmitError(null);
    setDirectUrl(null);

    // Client-side gate mirrors the server rules, for fast feedback only.
    const local = validateLead({ fullName, workEmail });
    if (!local.ok) {
      setFieldErrors({ [local.error.field]: local.error.message });
      (local.error.field === 'fullName' ? nameRef : emailRef).current?.focus();
      return;
    }
    setFieldErrors({});

    // The download target must exist in the predefined registry.
    if (!resource) {
      setSubmitError('That document is no longer available. Please contact us for a copy.');
      return;
    }

    inFlight.current = true;
    setPhase('submitting');

    try {
      // 1) Deliver the lead notification via FormSubmit. The download is gated
      //    on this succeeding — but a retry after a failed FILE fetch skips
      //    straight to the download so the visitor is not reported twice.
      if (!notified.current) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

        let accepted = false;
        let serverError: { message: string; field?: 'fullName' | 'workEmail' } | null = null;
        try {
          // Same-origin request — client-side blockers cannot touch it. The
          // route re-validates everything and only reports success once
          // FormSubmit's response body confirmed the notification.
          const res = await fetch(SUBMIT_LEAD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fullName: local.value.fullName,
              workEmail: local.value.workEmail,
              resourceId: resource.id,
              sourcePage: window.location.href,
            }),
            signal: controller.signal,
          });
          const data = await res.json().catch(() => null);
          accepted = res.ok && data?.success === true;
          if (!accepted) {
            console.error('[LeadCaptureModal] lead submission rejected:', res.status, data);
            if (data && typeof data.error === 'string') {
              serverError = {
                message: data.error,
                field:
                  data.field === 'fullName' || data.field === 'workEmail' ? data.field : undefined,
              };
            }
          }
        } finally {
          clearTimeout(timer);
        }

        if (!accepted) {
          if (serverError?.field) {
            setFieldErrors({ [serverError.field]: serverError.message });
            (serverError.field === 'fullName' ? nameRef : emailRef).current?.focus();
          } else {
            setSubmitError(
              serverError?.message ??
                'We could not submit your details. Please check your connection and try again.'
            );
          }
          return; // entered values are deliberately preserved for retry
        }

        notified.current = true;
      }

      // 2) Details are delivered. Only now do we fetch the document.
      setPhase('preparing-download');

      const downloadUrl = `/api/download?resourceId=${encodeURIComponent(resource.id)}`;

      /** Native browser download — handled by the browser's download manager,
          which antivirus/web filters cooperate with instead of emptying the
          response body the way they can with a scripted fetch(). The route
          sets Content-Disposition, so the filename is preserved. */
      const triggerNativeDownload = () => {
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = resource.downloadFilename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      };

      const res = await fetch(downloadUrl, { headers: { Accept: 'application/pdf' } });

      if (!res.ok) {
        console.error('[LeadCaptureModal] download route responded', res.status);
        setSubmitError(
          'Your details were submitted, but the document could not be retrieved. Please try again.'
        );
        return;
      }

      const blob = await res.blob();

      // Guard: never save an HTML error page or a gutted response as a .pdf.
      // The file's own magic number is checked instead of the Content-Type
      // header, because security proxies / antivirus web filters sometimes
      // rewrite headers — or empty the body — of otherwise-intact responses.
      const head =
        blob.size >= 5 ? new TextDecoder().decode(await blob.slice(0, 5).arrayBuffer()) : '';
      if (!head.startsWith('%PDF-')) {
        console.error(
          '[LeadCaptureModal] scripted fetch returned an unusable body (size:',
          blob.size,
          'content-type:',
          res.headers.get('content-type'),
          ') — falling back to a native browser download.'
        );
        // The lead IS delivered, so the visitor is entitled to the file.
        // Hand the download to the browser itself and keep a manual link
        // visible in case even that needs a user click.
        triggerNativeDownload();
        setDirectUrl(downloadUrl);
        setSubmitError(
          'Your details were submitted. If the download did not start automatically, use the direct download link below.'
        );
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = resource.downloadFilename;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Revoke on the next tick so the browser has committed the download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);

      // Success: clear the visitor's details before showing the done panel.
      setFullName('');
      setWorkEmail('');
      setEmailCheck({ status: 'idle' });
      setPhase('done');
    } catch (err) {
      // Always logged (nothing private) so failures are diagnosable in
      // production DevTools.
      console.error('[LeadCaptureModal] submission failed', err);
      if (notified.current && resource) {
        // The lead was already delivered — only the file transfer failed.
        setDirectUrl(`/api/download?resourceId=${encodeURIComponent(resource.id)}`);
        setSubmitError(
          'Your details were submitted, but the document could not be retrieved. Use the direct download link below, or try again.'
        );
      } else {
        setSubmitError(
          'We could not submit your details. Please check your connection and try again.'
        );
      }
    } finally {
      inFlight.current = false;
      setPhase((p) => (p === 'done' ? 'done' : 'idle'));
    }
  };

  if (!isOpen) return null;

  const submitDisabled = busy || !fullName.trim() || !workEmail.trim();

  return (
    <div
      className="m-fade fixed inset-0 z-50 flex items-end justify-center overflow-y-auto overscroll-contain bg-slate-950/40 p-3 backdrop-blur-[2px] sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        /* `.rise` is a 12px vertical lift — no tiny-scale "pop". */
        className="rise max-h-[90dvh] w-full overflow-y-auto rounded-xl border border-border bg-background shadow-lg sm:max-h-[88dvh] sm:max-w-[26rem]"
      >
        <div className="p-5 sm:p-8">
          {phase === 'done' ? (
            <div className="text-center py-6">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
                <Icon name="CheckIcon" size={28} className="text-success" />
              </div>
              <h2 id={titleId} className="t-h3 text-foreground mb-2">
                {successTitle}
              </h2>
              <p id={descriptionId} className="text-muted-foreground break-words">
                {successMessage}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="press mt-6 inline-flex min-h-[48px] w-full items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors duration-fast hover:bg-primary/90"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h2 id={titleId} className="t-h3 text-foreground break-words">
                  {modalTitle}
                </h2>
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={busy}
                  className="shrink-0 -mr-1 -mt-1 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                  aria-label="Close dialog"
                >
                  <Icon name="XMarkIcon" size={24} />
                </button>
              </div>

              <p id={descriptionId} className="text-sm text-muted-foreground mb-4 break-words">
                {modalDescription}
              </p>

              {/* The selected document, resolved from the predefined registry. */}
              <div className="mb-6 flex items-center gap-2.5 rounded-md border border-border bg-surface-2 px-3.5 py-2.5">
                <Icon name="DocumentTextIcon" size={18} className="shrink-0 text-accent-strong" />
                <span className="min-w-0 break-words text-sm font-medium text-foreground">
                  {resource ? resource.title : 'Document unavailable'}
                </span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label
                    htmlFor="lead-fullName"
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    Full Name <span aria-hidden="true">*</span>
                  </label>
                  <input
                    ref={nameRef}
                    id="lead-fullName"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    required
                    maxLength={100}
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      if (fieldErrors.fullName)
                        setFieldErrors((p) => ({ ...p, fullName: undefined }));
                    }}
                    placeholder="Jane Doe"
                    aria-invalid={Boolean(fieldErrors.fullName)}
                    aria-describedby={fieldErrors.fullName ? 'lead-fullName-error' : undefined}
                    className={`w-full min-h-[46px] border px-3.5 py-3 text-base ${
                      fieldErrors.fullName ? 'border-red-500' : 'border-border'
                    } rounded-md bg-surface-2 text-foreground transition-[border-color,background-color] duration-fast focus:bg-background focus:outline-none focus:ring-2 focus:ring-accent/40`}
                  />
                  {fieldErrors.fullName && (
                    <p id="lead-fullName-error" className="text-sm text-red-500 mt-1 break-words">
                      {fieldErrors.fullName}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="lead-workEmail"
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    Work Email Address <span aria-hidden="true">*</span>
                  </label>
                  <div className="relative">
                    <input
                      ref={emailRef}
                      id="lead-workEmail"
                      name="workEmail"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      maxLength={254}
                      value={workEmail}
                      onChange={(e) => {
                        setWorkEmail(e.target.value);
                        if (fieldErrors.workEmail)
                          setFieldErrors((p) => ({ ...p, workEmail: undefined }));
                      }}
                      placeholder="you@company.com"
                      aria-invalid={Boolean(fieldErrors.workEmail)}
                      aria-describedby={
                        fieldErrors.workEmail
                          ? 'lead-workEmail-error'
                          : emailCheck.status !== 'idle'
                            ? 'lead-workEmail-status'
                            : undefined
                      }
                      className={`w-full min-h-[46px] border px-3.5 py-3 pr-11 text-base ${
                        fieldErrors.workEmail || emailCheck.status === 'invalid'
                          ? 'border-red-500'
                          : emailCheck.status === 'valid'
                            ? 'border-green-500'
                            : 'border-border'
                      } rounded-md bg-surface-2 text-foreground transition-[border-color,background-color] duration-fast focus:bg-background focus:outline-none focus:ring-2 focus:ring-accent/40`}
                    />
                    {emailCheck.status !== 'idle' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2" aria-hidden="true">
                        {emailCheck.status === 'checking' && (
                          <span className="inline-block animate-spin motion-reduce:animate-none">
                            <Icon name="ArrowPathIcon" size={20} className="text-accent" />
                          </span>
                        )}
                        {emailCheck.status === 'valid' && (
                          <Icon name="CheckCircleIcon" size={20} className="text-green-500" />
                        )}
                        {emailCheck.status === 'invalid' && (
                          <Icon name="ExclamationCircleIcon" size={20} className="text-red-500" />
                        )}
                      </div>
                    )}
                  </div>

                  {fieldErrors.workEmail ? (
                    <p id="lead-workEmail-error" className="text-sm text-red-500 mt-1 break-words">
                      {fieldErrors.workEmail}
                    </p>
                  ) : (
                    emailCheck.status !== 'idle' && (
                      <p
                        id="lead-workEmail-status"
                        className={`text-sm mt-1 break-words ${
                          emailCheck.status === 'invalid'
                            ? 'text-red-500'
                            : emailCheck.status === 'valid'
                              ? 'text-green-600'
                              : 'text-muted-foreground'
                        }`}
                      >
                        {emailCheck.status === 'checking' && 'Checking address…'}
                        {emailCheck.status === 'valid' &&
                          (emailCheck.externallyVerified
                            ? 'Address verified.'
                            : 'Address looks valid.')}
                        {emailCheck.status === 'invalid' && emailCheck.message}
                      </p>
                    )
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitDisabled}
                  className="press mt-6 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-accent px-6 text-sm font-semibold text-accent-foreground transition-colors duration-fast hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy && (
                    <span className="inline-block animate-spin motion-reduce:animate-none">
                      <Icon name="ArrowPathIcon" size={18} />
                    </span>
                  )}
                  <span className="break-words">
                    {phase === 'submitting'
                      ? 'Submitting…'
                      : phase === 'preparing-download'
                        ? 'Preparing download…'
                        : buttonText}
                  </span>
                </button>

                <div aria-live="polite" role="status" className="min-h-[1.25rem]">
                  {submitError && (
                    <p className="text-sm text-red-500 mt-2 break-words">{submitError}</p>
                  )}
                  {directUrl && (
                    <a
                      href={directUrl}
                      download={resource?.downloadFilename}
                      rel="noopener"
                      className="mt-2 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-accent-strong transition-colors duration-fast hover:text-accent"
                    >
                      <Icon name="ArrowDownTrayIcon" size={17} className="shrink-0" />
                      Direct download: {resource?.downloadFilename}
                    </a>
                  )}
                </div>

                <p className="text-xs leading-relaxed text-muted-foreground">
                  Your details will be used to process this download request and contact you
                  regarding the requested document.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
